import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import BehaviorNav from "./BehaviorNav";

const RANGES = ["7d", "30d", "90d"];
const SORTS = [
  { value: "engagement", label: "Engagement" },
  { value: "visitors", label: "Visitors" },
  { value: "pageviews", label: "Pageviews" },
  { value: "time", label: "Avg Time" },
  { value: "scroll", label: "Scroll Depth" },
];

function EngagementBadge({ score }) {
  if (score == null) return <span className="text-gray-500">—</span>;
  let cls = "text-green-400 bg-green-900/20";
  if (score < 40) cls = "text-red-400 bg-red-900/20";
  else if (score < 70) cls = "text-amber-400 bg-amber-900/20";
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${cls}`}>
      {score.toFixed(0)}
    </span>
  );
}

export default function BehaviorPages({ selectedSite }) {
  const [pages, setPages] = useState([]);
  const [range, setRange] = useState("7d");
  const [sort, setSort] = useState("engagement");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get("/behavior/pages/", {
        params: { website_id: selectedSite.id, range, sort },
      })
      .then((res) => setPages(res.data.results || res.data))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, [selectedSite, range, sort]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view page analytics
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Page Analytics</h2>
        <div className="flex items-center gap-3">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-gray-300"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                Sort: {s.label}
              </option>
            ))}
          </select>
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  range === r
                    ? "bg-copper/20 text-copper"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      <BehaviorNav />

      {loading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-12 bg-dark-300 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2.5 px-4 font-medium">Path</th>
                  <th className="text-right py-2.5 px-3 font-medium">Visitors</th>
                  <th className="text-right py-2.5 px-3 font-medium">Pageviews</th>
                  <th className="text-right py-2.5 px-3 font-medium">Avg Time</th>
                  <th className="text-right py-2.5 px-3 font-medium">Avg Scroll%</th>
                  <th className="text-right py-2.5 px-3 font-medium">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((p, i) => (
                  <tr
                    key={i}
                    onClick={() =>
                      navigate(`/behavior/page/${encodeURIComponent(p.path)}`)
                    }
                    className="border-b border-dark-50/50 hover:bg-dark-50/30 cursor-pointer"
                  >
                    <td className="py-2.5 px-4 font-mono text-xs text-gray-300 truncate max-w-xs">
                      {p.path}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {p.visitors?.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {p.pageviews?.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {p.avg_time != null ? `${p.avg_time.toFixed(1)}s` : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {p.avg_scroll != null ? `${p.avg_scroll.toFixed(0)}%` : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <EngagementBadge score={p.engagement_score} />
                    </td>
                  </tr>
                ))}
                {!pages.length && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-gray-500">
                      No page data yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
